import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@creanizer/ui'
import { FileText, Search, Trash2, Copy, Edit2, Calendar, Layout, Palette, Hash, RefreshCw, Download, Upload } from 'lucide-react'
import { useScenarioStore } from '../../../../stores/scenarioStore'
// Toast notifications removed
import { formatDistanceToNow } from 'date-fns'
import { ja } from 'date-fns/locale'

interface SavedFormatsListProps {
  onEdit?: (formatId: string) => void
}

export function SavedFormatsList({ onEdit }: SavedFormatsListProps = {}) {
  const { formats, removeFormat, updateFormat, addFormat } = useScenarioStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [editingFormat, setEditingFormat] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [formatToDelete, setFormatToDelete] = useState<string | null>(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importData, setImportData] = useState('')
  
  const filteredFormats = formats.filter(format => {
    const query = searchQuery.toLowerCase()
    return (
      format.name.toLowerCase().includes(query) ||
      format.result?.formatInfo?.description?.toLowerCase().includes(query) ||
      format.result?.formatInfo?.style?.toLowerCase().includes(query)
    )
  })
  
  const handleCopy = (format: typeof formats[0]) => {
    const jsonData = JSON.stringify(format.result, null, 2)
    navigator.clipboard.writeText(jsonData)
    console.log('フォーマットデータをコピーしました')
  }
  
  const handleEdit = (format: typeof formats[0]) => {
    setEditingFormat(format.id)
    setEditName(format.name)
  }
  
  const handleSaveEdit = (id: string) => {
    if (editName.trim()) {
      const format = formats.find(f => f.id === id)
      if (format) {
        updateFormat(id, { ...format, name: editName.trim() })
        console.log('フォーマット名を更新しました')
      }
    }
    setEditingFormat(null)
    setEditName('')
  }
  
  const handleCancelEdit = () => {
    setEditingFormat(null)
    setEditName('')
  }
  
  const handleDelete = (id: string) => {
    setFormatToDelete(id)
    setDeleteDialogOpen(true)
  }
  
  const confirmDelete = () => {
    if (formatToDelete) {
      removeFormat(formatToDelete)
      console.log('フォーマットを削除しました')
      setFormatToDelete(null)
    }
    setDeleteDialogOpen(false)
  }
  
  // 個別フォーマットのエクスポート
  const handleExportFormat = (format: typeof formats[0]) => {
    const exportData = {
      type: 'creanizer-format',
      version: '1.0',
      exportDate: new Date().toISOString(),
      format: format
    }
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `format_${format.name.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    console.log('フォーマットをエクスポートしました')
  }
  
  // 全フォーマットのエクスポート
  const handleExportAll = () => {
    const exportData = {
      type: 'creanizer-formats-bulk',
      version: '1.0',
      exportDate: new Date().toISOString(),
      formats: formats
    }
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `all_formats_${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    console.log(`${formats.length}個のフォーマットをエクスポートしました`)
  }
  
  // インポート処理
  const handleImport = () => {
    try {
      const data = JSON.parse(importData)
      if (data.type === 'creanizer-format' && data.format) {
        // 単一フォーマットのインポート
        addFormat({
          ...data.format,
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
        console.log('1個のフォーマットをインポートしました')
      } else if (data.type === 'creanizer-formats-bulk' && data.formats) {
        // 複数フォーマットのインポート
        data.formats.forEach((format: any) => {
          addFormat({
            ...format,
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        })
        console.log(`${data.formats.length}個のフォーマットをインポートしました`)
      } else {
        throw new Error('無効なファイル形式です')
      }
      setImportDialogOpen(false)
      setImportData('')
    } catch (error) {
      console.error('インポートエラー:', error)
      alert('インポートに失敗しました。ファイル形式を確認してください。')
    }
  }
  
  // ファイル選択処理
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setImportData(event.target?.result as string)
      }
      reader.readAsText(file)
    }
  }
  
  const getFormatFeatures = (result: any) => {
    const features = []
    if (result?.structure) features.push({ icon: Layout, label: '構造' })
    if (result?.writingStyle) features.push({ icon: Palette, label: '文体' })
    if (result?.patterns) features.push({ icon: Hash, label: 'パターン' })
    return features
  }
  
  const getComplexityLabel = (complexity?: string) => {
    const labels: Record<string, string> = {
      simple: 'シンプル',
      moderate: '中程度',
      complex: '複雑',
      very_complex: '非常に複雑'
    }
    return labels[complexity || ''] || complexity
  }
  
  if (formats.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">保存されたフォーマットはありません</p>
          <p className="text-sm text-gray-400 mt-2">
            フォーマットを解析して保存すると、ここに表示されます
          </p>
        </CardContent>
      </Card>
    )
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="フォーマットを検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setImportDialogOpen(true)}
        >
          <Upload className="w-4 h-4 mr-2" />
          インポート
        </Button>
        {formats.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportAll}
          >
            <Download className="w-4 h-4 mr-2" />
            全てエクスポート
          </Button>
        )}
        <Badge variant="secondary">
          {filteredFormats.length} / {formats.length} 件
        </Badge>
      </div>
      
      <ScrollArea className="h-[600px]">
        <div className="space-y-3 pr-4">
          {filteredFormats.map((format) => {
            const features = getFormatFeatures(format.result)
            
            return (
              <Card key={format.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      {editingFormat === format.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(format.id)
                              if (e.key === 'Escape') handleCancelEdit()
                            }}
                            className="h-8"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(format.id)}
                          >
                            保存
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancelEdit}
                          >
                            キャンセル
                          </Button>
                        </div>
                      ) : (
                        <>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            {format.name}
                          </CardTitle>
                          {format.result?.formatInfo?.description && (
                            <p className="text-sm text-gray-500 mt-1">
                              {format.result.formatInfo.description}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    {!editingFormat && (
                      /* デスクトップ版: 縦並びボタン */
                      <div className="hidden sm:flex gap-1">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(format.id)}
                            title="解析結果を編集"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(format)}
                          title="名前を編集"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(format)}
                          title="JSONをコピー"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportFormat(format)}
                          title="エクスポート"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(format.id)}
                          title="削除"
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* モバイル版: 横一列のボタン */}
                  {!editingFormat && (
                    <div className="flex sm:hidden gap-1 mb-3">
                      {onEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(format.id)}
                          title="解析編集"
                          className="flex-1"
                        >
                          <RefreshCw className="w-4 h-4" />
                          <span className="ml-1">解析</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(format)}
                        title="編集"
                        className="flex-1"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="ml-1">名前</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(format)}
                        title="コピー"
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4" />
                        <span className="ml-1">コピー</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(format.id)}
                        title="削除"
                        className="flex-1 text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="ml-1">削除</span>
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                    <div className="flex flex-wrap gap-2">
                      {features.map((feature, index) => {
                        const Icon = feature.icon
                        return (
                          <Badge key={index} variant="outline">
                            <Icon className="w-3 h-3 mr-1" />
                            {feature.label}
                          </Badge>
                        )
                      })}
                      {format.result?.formatInfo?.style && (
                        <Badge>
                          {format.result.formatInfo.style}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-2 sm:mt-0">
                      <Calendar className="w-3 h-3" />
                      {formatDistanceToNow(new Date(format.createdAt), { 
                        addSuffix: true,
                        locale: ja 
                      })}
                    </div>
                  </div>
                  
                  {format.result?.structure?.sections && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-500">
                        セクション数: {format.result.structure.sections.length}
                      </span>
                    </div>
                  )}
                  
                  {format.result?.formatInfo && (
                    <div className="flex flex-wrap gap-2 text-xs">
                      {format.result.formatInfo.bestFor && format.result.formatInfo.bestFor.length > 0 && (
                        <Badge variant="secondary">
                          {format.result.formatInfo.bestFor[0]}
                        </Badge>
                      )}
                    </div>
                  )}
                  
                  {format.result?.elements?.essential && format.result.elements.essential.length > 0 && (
                    <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <p className="text-xs font-semibold mb-1">必須要素:</p>
                      <div className="flex flex-wrap gap-1">
                        {format.result.elements.essential.slice(0, 5).map((element: string, index: number) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {element}
                          </Badge>
                        ))}
                        {format.result.elements.essential.length > 5 && (
                          <span className="text-xs text-gray-500">
                            +{format.result.elements.essential.length - 5}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </ScrollArea>
      
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>フォーマットを削除しますか？</DialogTitle>
            <DialogDescription>
              この操作は取り消せません。フォーマットデータは完全に削除されます。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
            <Button onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              削除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>フォーマットをインポート</DialogTitle>
            <DialogDescription>
              エクスポートしたJSONファイルを選択するか、JSONデータを直接貼り付けてください。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="file-input">ファイルを選択</Label>
              <Input
                id="file-input"
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="json-input">またはJSONを貼り付け</Label>
              <Textarea
                id="json-input"
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="JSONデータを貼り付けてください..."
                rows={10}
                className="mt-2 font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setImportDialogOpen(false)
              setImportData('')
            }}>
              キャンセル
            </Button>
            <Button onClick={handleImport} disabled={!importData}>
              インポート
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}